import TdsProfileSection from '../../components/dashboard/TdsProfileSection'
import { extractProfileDataApi } from '../../utils/tdsFileHandlers'
import { getStorageItem, setStorageItem, getLegacyStorageKey } from '../../utils/storage';
import { getTdsFormRoute } from '../../utils/tdsCommon'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { FileText, User, Upload, ArrowLeft, Building2, X, Trash2, Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import axios from 'axios'
import TdsDetailsTable from '../../components/dashboard/TdsDetailsTable'
import ChallanDetailsTable from '../../components/dashboard/ChallanDetailsTable'
import TdsSidebar from '../../components/dashboard/TdsSidebar'
import TdsGeneratedFiles from './TdsGeneratedFiles';
import Tds26QCorrectionForm from './Tds26QCorrectionForm';
import { useError } from '../../contexts/ErrorContext'


const stateOptions = [
  'ANDAMAN AND NICOBAR ISLANDS',
  'ANDHRA PRADESH',
  'ARUNACHAL PRADESH',
  'ASSAM',
  'BIHAR',
  'CHANDIGARH',
  'CHHATTISGARH',
  'DADRA & NAGAR HAVELI AND DAMAN & DIU',
  'DELHI',
  'GOA',
  'GUJARAT',
  'HARYANA',
  'HIMACHAL PRADESH',
  'JAMMU & KASHMIR',
  'JHARKHAND',
  'KARNATAKA',
  'KERALA',
  'LADAKH',
  'LAKSHWADEEP',
  'MADHYA PRADESH',
  'MAHARASHTRA',
  'MANIPUR',
  'MEGHALAYA',
  'MIZORAM',
  'NAGALAND',
  'ODISHA',
  'PONDICHERRY',
  'PUNJAB',
  'RAJASTHAN',
  'SIKKIM',
  'TAMIL NADU',
  'TELANGANA',
  'TRIPURA',
  'UTTAR PRADESH',
  'UTTARAKHAND',
  'WEST BENGAL'
];

const Tds26QForm = ({ isMobile, onSidebarStateChange }) => {
  console.log('Rendering Tds26QForm component');
  const navigate = useNavigate()
  const { addError } = useError()
  const [activeSection, setActiveSection] = useState('profile')
  const [challanData, setChallanData] = useState([])
  const [tdsData, setTdsData] = useState([])
  const [originalTdsData, setOriginalTdsData] = useState([]) // For tracking changes in Revised Return

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarState, setSidebarState] = useState({
    isOpen: false,
    isExpanded: false,
    keepOpen: false,
    hovered: false
  })

  // Financial Year, Quarter and Form selection
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('')
  const [selectedQuarter, setSelectedQuarter] = useState('')
  const [selectedForm, setSelectedForm] = useState('')
  const [selectedReturnType, setSelectedReturnType] = useState('')


  // Validation state
  const [isValidating, setIsValidating] = useState(false)

  // Handle Unified Import Event from Sidebar
  useEffect(() => {
    const handleImport = (e) => {
      const { challans, deductees } = e.detail;
      if (challans && challans.length > 0) {
        setChallanData(challans); // Overwrite existing data
        toast.success(`Imported ${challans.length} Challans`);
      } else if (challans) {
        // If explicit empty array or overwrite intent for empty file
        setChallanData([]);
      }

      if (deductees && deductees.length > 0) {
        setTdsData(deductees); // Overwrite existing data
        toast.success(`Imported ${deductees.length} Deductees`);
      } else if (deductees) {
        setTdsData([]);

      }
    };

    window.addEventListener('importTdsData', handleImport);
    return () => window.removeEventListener('importTdsData', handleImport);
  }, []);

  // CSI File Upload state
  const [csiFile, setCsiFile] = useState(null)
  const csiFileInputRef = React.useRef(null)
  const [showCsiUploadPopup, setShowCsiUploadPopup] = useState(false)

  // CSI Download Automation State
  const [showDownloadPrompt, setShowDownloadPrompt] = useState(false);
  const [downloadPassword, setDownloadPassword] = useState('');
  const [showDownloadPassword, setShowDownloadPassword] = useState(false);
  const [isDownloadingCsi, setIsDownloadingCsi] = useState(false);

  // Profile Pre-fill Automation State
  const [showPrefillPrompt, setShowPrefillPrompt] = useState(false);
  const [prefillPassword, setPrefillPassword] = useState('');
  const [showPrefillPassword, setShowPrefillPassword] = useState(false);
  const [isExtractingProfile, setIsExtractingProfile] = useState(false);

  const handleProfilePrefill = async () => {
    if (!prefillPassword) {
      toast.error('Portal password is required');
      return;
    }

    setIsExtractingProfile(true);
    try {
      const { success, data, error } = await extractProfileDataApi({
        tan: profileData?.section1?.taxDeduction,
        password: prefillPassword
      });

      if (success && data) {
        const newData = { ...profileData };
        if (data.pan) {
          if (newData.section1) newData.section1.permanentAccountNumber = data.pan;
          if (newData.section3) newData.section3.permanentAccountNumber = data.pan;
        }
        if (data.name) {
          if (newData.section2) newData.section2.name = data.name;
        }
        if (data.keyPerson) {
          if (data.keyPerson.name && newData.section3) newData.section3.name = data.keyPerson.name;
          if (data.keyPerson.pan && newData.section3) newData.section3.permanentAccountNumber = data.keyPerson.pan;
        }
        if (data.email) {
          if (newData.section2) newData.section2.email = data.email;
          if (newData.section3) newData.section3.email = data.email;
        }
        if (data.mobile) {
          if (newData.section2) newData.section2.mobileNo = data.mobile;
          if (newData.section3) newData.section3.mobileNo = data.mobile;
        }
        if (data.address) {
          if (newData.section2) {
            newData.section2.flatNo = data.address.flatNo || '';
            newData.section2.nameOfPremises = data.address.premisesName || '';
            newData.section2.roadStreet = data.address.roadStreet || '';
            newData.section2.areaLocality = data.address.areaLocality || '';
            newData.section2.townCity = data.address.city || '';
            newData.section2.pinCode = data.address.pinCode || '';
            if (data.address.state) {
                newData.section2.state = data.address.state.toUpperCase().trim();
            }
          }
        }

        if (typeof setProfileData === 'function') {
          setProfileData(newData);
        }
        toast.success('Profile Data Auto-Filled Successfully!');
        setShowPrefillPrompt(false);
        setPrefillPassword('');
      } else {
        toast.error(error || 'Failed to extract profile data');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred during extraction');
    } finally {
      setIsExtractingProfile(false);
    }
  };

  // Handle CSI Download
  const handleCsiDownload = async () => {
    if (!downloadPassword) {
      addError('Password is required');
      return;
    }

    setIsDownloadingCsi(true);
    try {
      const response = await axios.post('/api/login-income-tax', {
        credentials: {
          tan: profileData.section1.taxDeduction,
          password: downloadPassword
        },
        dateRange: challanDateRange
      });

      if (response.data.success && response.data.csiContent) {
        toast.success('CSI File Downloaded Successfully!');
        setShowDownloadPrompt(false);
        setDownloadPassword('');
        
        const binaryString = window.atob(response.data.csiContent.replace(/\s/g, ''));
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const file = new File([bytes], response.data.fileName || 'downloaded.csi', { type: 'text/plain' });
        if (typeof setCsiFile === 'function') setCsiFile(file);
        if (typeof executeValidation === 'function') executeValidation(file);
      } else {
        const errMsg = response.data.error || 'Failed to download CSI file. Please check password.';
        toast.error(errMsg);
      }
    } catch (error) {
      addError('Download Failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsDownloadingCsi(false);
    }
  };

  const handleSkipDownload = () => {
    setShowDownloadPrompt(false);
    handleValidateFile();
  };

  const handleCancelValidation = async () => {
    try {
      await axios.post('/api/cancel-validation');
      toast.info('Validation cancelled by user');
    } catch (err) {
      console.error('Error cancelling validation:', err);
      toast.error('Failed to cancel validation');
    } finally {
      setIsValidating(false);
    }
  };



  // Conso File Content State
  const [consoFileContent, setConsoFileContent] = useState(null)

  // Profile form data with simplified structure
  const [isProfileEditing, setIsProfileEditing] = useState(true) // Always in edit mode
  const [profileData, setProfileData] = useState({
    // Section 1: Particula
    section1: {
      taxDeduction: '',
      permanentAccountNumber: '',
      typeOfDeduction: '',
      receiptNoOriginal: '', // New Key
      receiptNoEarlier: '', // New Key
      isEarlierReturnFiled: 'Yes' // New Key
    },

    // Section 2: Particula
    section2: {
      name: '',
      branchDivision: '',
      flatNo: '',
      nameOfPremises: '',
      roadStreet: '',
      areaLocality: '',
      townCity: '',
      pinCode: '',
      state: '',
      email: '',
      stdCode: '',
      telephoneNo: ''
    },

    // Section 3: Particula
    section3: {
      sameAsAbove: false,
      name: '',
      designation: '',
      permanentAccountNumber: '',
      nameOfPremises: '',
      flatNo: '',
      areaLocality: '',
      roadStreet: '',
      pinCode: '',
      townCity: '',
      telephone: '',
      telephoneAlternate: '',
      state: '',
      email: '',
      emailAlternate: '',
      mobileNo: '',
      hasAddressChanged: '',
      // duplicate removed
    }
  })


  // Calculate Date Range for CSI File Download
  const challanDateRange = React.useMemo(() => {
    if (!challanData || challanData.length === 0) return { from: '', to: '' };

    // Challan Date format is often DD-MM-YYYY or YYYY-MM-DD depending on input
    // Assuming YYYY-MM-DD based on <input type="date"> used in grid or standard format
    // But ExcelDataGrid might store it differently. Let's parse carefully.

    const dates = challanData
      .map(c => c.dateOfDeposit)
      .filter(d => d) // Remove empty
      .map(d => new Date(d))
      .filter(d => !isNaN(d.getTime()));

    if (dates.length === 0) return { from: '', to: '' };

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    // Format as DD-MM-YYYY (or YYYY-MM-DD as needed by automation props, normally standard ISO string is safer for transport)
    // The automation script might need reformatting. Let's send ISO YYYY-MM-DD for consistency.
    const toISODate = (d) => d.toISOString().split('T')[0];

    return {
      from: toISODate(minDate),
      to: toISODate(maxDate)
    };
  }, [challanData]);
  // Load data from storage/database on component mount
  useEffect(() => {
    (async () => {
      const cid = localStorage.getItem('activeClientId') || 'guest';
      const savedFinancialYear = localStorage.getItem('tdsFinancialYear');
      const savedQuarter = localStorage.getItem('tdsQuarter');
      const fy = savedFinancialYear || 'default_fy';
      const qtr = savedQuarter || 'default_qtr';

      const savedChallanData = await getStorageItem(`26Q_${cid}_${fy}_${qtr}_ChallanData`);
      const savedTdsData = await getStorageItem(`26Q_${cid}_${fy}_${qtr}_TdsData`);
      const savedOriginalTdsData = await getStorageItem(`26Q_${cid}_${fy}_${qtr}_OriginalTdsData`);
      const savedProfileData = await getStorageItem(`26Q_${cid}_ProfileData`);
      const cloudReturnType = await getStorageItem(`26Q_${cid}_${fy}_${qtr}_ReturnType`);
      const savedReturnType = cloudReturnType || localStorage.getItem('SELECTED_RETURN_TYPE');
      const savedForm = localStorage.getItem('tdsForm');

      if (savedChallanData) setChallanData(savedChallanData);
      if (savedTdsData) setTdsData(savedTdsData);
      if (savedOriginalTdsData) {
        setOriginalTdsData(typeof savedOriginalTdsData === 'string' ? JSON.parse(savedOriginalTdsData) : savedOriginalTdsData);
      }

      if (savedProfileData) {
        try {
          const parsedData = typeof savedProfileData === 'string' ? JSON.parse(savedProfileData) : savedProfileData;
          if (parsedData && parsedData.section1 && parsedData.section2 && parsedData.section3) {
            setProfileData(parsedData);
          }
        } catch (error) {
          console.error('Error parsing profile data from storage:', error);
        }
      }

      if (savedFinancialYear) setSelectedFinancialYear(savedFinancialYear);
      if (savedQuarter) setSelectedQuarter(savedQuarter);

      const activeYear = savedFinancialYear || '2026-27';
      const isNewFormat = activeYear ? parseInt(activeYear.split('-')[0]) >= 2026 : false;
      let initialForm = isNewFormat ? '140' : '26Q';
      if (savedForm === '26Q' || savedForm === '140') {
        initialForm = isNewFormat ? '140' : '26Q';
      }
      setSelectedForm(initialForm);
      localStorage.setItem('tdsForm', initialForm);

      if (savedReturnType) setSelectedReturnType(savedReturnType);
    })();
  }, []);



  // Prepare Return functionality
  React.useEffect(() => {
    const handlePrepareReturn = () => {
      handlePrepareReturnFile()
    }

    window.addEventListener('prepareReturn', handlePrepareReturn)
    return () => window.removeEventListener('prepareReturn', handlePrepareReturn)
  }, [profileData, challanData, tdsData, selectedFinancialYear, selectedQuarter, selectedForm])






  const handleChallanDataChange = (data) => {
    setChallanData(data)
    localStorage.setItem(getLegacyStorageKey('26Q', 'ChallanData'), JSON.stringify(data))
  }

  const handleTdsDataChange = (data) => {
    setTdsData(data)
    localStorage.setItem(getLegacyStorageKey('26Q', 'TdsData'), JSON.stringify(data))
  }


  const handleSectionChange = (section) => {
    setActiveSection(section)
  }

  const handleSidebarClose = () => {
    setSidebarOpen(false)
  }

  const handleSidebarStateChange = useCallback((state) => {
    setSidebarState(state)
  }, [])



  // Sync Section 3 address fields with Section 2 when "Same as above" is checked
  useEffect(() => {
    // Add safety checks to ensure section2 and section3 exist
    if (
      profileData?.section3?.sameAsAbove &&
      isProfileEditing &&
      profileData?.section2 &&
      profileData?.section3
    ) {
      const newData = { ...profileData }
      // Ensure section2 and section3 exist in newData
      if (!newData.section2) newData.section2 = {}
      if (!newData.section3) newData.section3 = {}

      newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
      newData.section3.flatNo = newData.section2.flatNo || ''
      newData.section3.areaLocality = newData.section2.areaLocality || ''
      newData.section3.roadStreet = newData.section2.roadStreet || ''
      newData.section3.townCity = newData.section2.townCity || ''
      newData.section3.pinCode = newData.section2.pinCode || ''
      newData.section3.state = newData.section2.state || ''
      newData.section3.email = newData.section2.email || ''
      newData.section3.stdCode = newData.section2.stdCode || ''
      newData.section3.telephone = newData.section2.telephoneNo || ''
      setProfileData(newData)
      // Cloud sync via updateProfileData, JSON.stringify(newData))
    }
  }, [
    profileData?.section2?.nameOfPremises,
    profileData?.section2?.flatNo,
    profileData?.section2?.areaLocality,
    profileData?.section2?.roadStreet,
    profileData?.section2?.townCity,
    profileData?.section2?.pinCode,
    profileData?.section2?.state,
    profileData?.section2?.email,
    profileData?.section2?.stdCode,
    profileData?.section2?.telephoneNo,
    profileData?.section3?.sameAsAbove,
    isProfileEditing
  ])

  // Profile form handlers
  const handleProfileInputChange = (field, value) => {
    const keys = field.split('.')
    const newData = { ...profileData }

    // Ensure section1, section2, section3 exist
    if (!newData.section1) newData.section1 = {}
    if (!newData.section2) newData.section2 = {}
    if (!newData.section3) newData.section3 = {}

    let current = newData
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {}
      }
      current = current[keys[i]]
    }
    current[keys[keys.length - 1]] = value

    // If "Same as above" is checked, copy address fields from section2 to section3
    if (field === 'section3.sameAsAbove' && value === true && newData.section2 && newData.section3) {
      newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
      newData.section3.flatNo = newData.section2.flatNo || ''
      newData.section3.areaLocality = newData.section2.areaLocality || ''
      newData.section3.roadStreet = newData.section2.roadStreet || ''
      newData.section3.townCity = newData.section2.townCity || ''
      newData.section3.pinCode = newData.section2.pinCode || ''
      newData.section3.state = newData.section2.state || ''
      newData.section3.email = newData.section2.email || ''
      newData.section3.stdCode = newData.section2.stdCode || ''
      newData.section3.telephone = newData.section2.telephoneNo || ''
      // Note: mobileNo is not in section2, so we don't copy it
    }
    setProfileData(newData)
    // Cloud sync via updateProfileData, JSON.stringify(newData))
  }



  // Validation function for Prepare Return
  const validateDataForReturn = () => {
    const errors = []

    // Check if at least one TDS entry exists
    if (!tdsData || tdsData.length === 0) {
      errors.push('At least one TDS entry is required')
    }

    // Check if at least one Challan entry exists
    if (!challanData || challanData.length === 0) {
      errors.push('At least one Challan entry is required')
    }

    // Receipt Details Validation
    if (selectedReturnType === 'Revised') {
      if (!profileData?.section1?.receiptNoOriginal) {
        errors.push('Receipt No. of Original Return is missing (Upload valid Conso File)')
      }
      if (!profileData?.section1?.receiptNoEarlier) {
        errors.push('Receipt No. of Earlier Statement is missing (Upload valid Conso File)')
      }
    } else if (selectedReturnType === 'Original') {
      if (profileData?.section1?.isEarlierReturnFiled === 'Yes' && !profileData?.section1?.receiptNoOriginal) {
        errors.push('Receipt No. of Original Return is required')
      }
    }

    // Validate profile data - check compulsory fields with safety checks
    if (!profileData?.section2?.name) {
      errors.push('Name is required in Profile Section 2')
    }
    if (!profileData?.section2?.flatNo) {
      errors.push('Flat No. is required in Profile Section 2')
    }
    if (!profileData?.section2?.state) {
      errors.push('State is required in Profile Section 2')
    }
    if (!profileData?.section2?.pinCode) {
      errors.push('PIN Code is required in Profile Section 2')
    }
    if (!profileData?.section3?.name) {
      errors.push('Responsible Person Name is required in Profile')
    }
    if (!profileData?.section3?.designation) {
      errors.push('Responsible Person Designation is required in Profile')
    }
    if (!profileData?.section3?.flatNo && !profileData?.section3?.nameOfPremises && !profileData?.section3?.roadStreet) {
      errors.push('Responsible Person Address is required in Profile')
    }
    if (!profileData?.section3?.state) {
      errors.push('Responsible Person State is required in Profile')
    }
    if (!profileData?.section3?.pinCode) {
      errors.push('Responsible Person Pincode is required in Profile')
    }
    if (!profileData?.section3?.email) {
      errors.push('Responsible Person Email is required in Profile')
    }
    if (!profileData?.section3?.mobileNo) {
      errors.push('Responsible Person Mobile is required in Profile')
    }
    if (!profileData?.section3?.permanentAccountNumber) {
      errors.push('Responsible Person PAN is required in Profile')
    }

    // Validate TDS data - check compulsory fields
    if (tdsData && tdsData.length > 0) {
      tdsData.forEach((row, index) => {
        if (!row.panOfDeductee) {
          errors.push(`TDS Entry ${index + 1}: PAN of Deductee is required`)
        }
        if (!row.sectionCode) {
          errors.push(`TDS Entry ${index + 1}: Section Code is required`)
        }
        if (row.tdsAmount === '' || row.tdsAmount === null || row.tdsAmount === undefined) {
          row.tdsAmount = 0;
        }
        if (!row.dateOfPayment) {
          errors.push(`TDS Entry ${index + 1}: Date of Payment is required`)
        }
        if (!row.dateOfDeduction) {
          errors.push(`TDS Entry ${index + 1}: Date of Deduction is required`)
        }
        if (parseFloat(row.tdsAmount) > 0 && !row.challanId) {
          errors.push(`TDS Entry ${index + 1}: Challan Selection is required`)
        }
      })
    }

    // Validate Challan data - check compulsory fields
    if (challanData && challanData.length > 0) {
      challanData.forEach((row, index) => {
        if (!row.tdsAmount || parseFloat(row.tdsAmount) <= 0) {
          errors.push(`Challan Entry ${index + 1}: TDS Amount is required`)
        }
        if (!row.dateOfDeposit) {
          errors.push(`Challan Entry ${index + 1}: Date of Deposit is required`)
        }
        if (!row.bsrCode) {
          errors.push(`Challan Entry ${index + 1}: BSR Code is required`)
        } else if (row.bsrCode.length !== 7) {
          errors.push(`Challan Entry ${index + 1}: BSR Code must be 7 digits`)
        }
        if (!row.challanSerialNo) {
          errors.push(`Challan Entry ${index + 1}: Challan Serial No. is required`)
        } else if (row.challanSerialNo.length !== 5) {
          errors.push(`Challan Entry ${index + 1}: Challan Serial No. must be 5 digits`)
        }
      })
    }

    return errors
  }

  // File generation function
  const generateTdsStatementFile = () => {
    const currentDate = new Date()
    const fileCreationDate = currentDate.toLocaleDateString('en-GB').replace(/\//g, '')

    // Get financial year for assessment year calculation
    let fyStartYear = 0
    let financialYear = ''

    if (selectedFinancialYear) {
      const parts = selectedFinancialYear.split('-')
      if (parts.length >= 2) {
        fyStartYear = parseInt(parts[0])
        // Handle both 2025-26 and 2025-2026 formats to produce 202526
        const yearPart2 = parts[1].length === 4 ? parts[1].slice(-2) : parts[1]
        financialYear = `${parts[0]}${yearPart2}`
      }
    }

    // Fallback if invalid
    if (!fyStartYear || isNaN(fyStartYear)) {
      const today = new Date()
      fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
      financialYear = `${fyStartYear}${String(fyStartYear + 1).slice(-2)}`
    }

    const ayStart = fyStartYear + 1
    const assessmentYear = `${ayStart}${String(ayStart + 1).slice(-2)}`

    console.log('Generating File with:', { selectedFinancialYear, financialYear, assessmentYear })

    // Format dates to ddmmyyyy
    const formatDate = (dateString) => {
      if (!dateString) return ''
      // Handle YYYY-MM-DD string directly to avoid timezone issues in different environments
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-')
        return `${day}${month}${year}`
      }
      // Fallback for other formats
      const date = new Date(dateString)
      const dayStr = String(date.getDate()).padStart(2, '0')
      const monthStr = String(date.getMonth() + 1).padStart(2, '0')
      const yearStr = date.getFullYear()
      return `${dayStr}${monthStr}${yearStr}`
    }

    // Format amounts to remove decimals and pad with zeros
    const formatAmount = (amount) => {
      const num = parseFloat(amount || 0)
      return Math.round(num).toString().padStart(15, '0')
    }

    // Format decimal amounts with 2 decimal places
    const formatDecimalAmount = (amount) => {
      const num = parseFloat(amount || 0)
      return num.toFixed(2)
    }

    // Truncate strings to max length (no padding needed for delimited file)
    const padString = (str, length) => {
      return String(str || '').substring(0, length)
    }

    // State Code Mapping
    const stateCodes = {
      'ANDAMAN AND NICOBAR ISLANDS': '01',
      'ANDHRA PRADESH': '02',
      'ARUNACHAL PRADESH': '03',
      'ASSAM': '04',
      'BIHAR': '05',
      'CHANDIGARH': '06',
      'DADRA & NAGAR HAVELI AND DAMAN & DIU': '07',
      'DELHI': '09',
      'GOA': '10',
      'GUJARAT': '11',
      'HARYANA': '12',
      'HIMACHAL PRADESH': '13',
      'JAMMU & KASHMIR': '14',
      'KARNATAKA': '15',
      'KERALA': '16',
      'LAKSHWADEEP': '17',
      'MADHYA PRADESH': '18',
      'MAHARASHTRA': '19',
      'MANIPUR': '20',
      'MEGHALAYA': '21',
      'MIZORAM': '22',
      'NAGALAND': '23',
      'ODISHA': '24',
      'PONDICHERRY': '25',
      'PUNJAB': '26',
      'RAJASTHAN': '27',
      'SIKKIM': '28',
      'TAMIL NADU': '29',
      'TRIPURA': '30',
      'UTTAR PRADESH': '31',
      'WEST BENGAL': '32',
      'CHHATTISGARH': '33',
      'UTTARAKHAND': '34',
      'JHARKHAND': '35',
      'TELANGANA': '36',
      'LADAKH': '37'
    }

    const getStateCode = (stateName) => {
      if (!stateName) return ''
      const upperName = stateName.toUpperCase().trim()
      // Direct match
      if (stateCodes[upperName]) return stateCodes[upperName]
      // Case insensitive check (already done by upper) or partial?
      // Fallback: Check if stateName is already a code?
      if (/^\d+$/.test(upperName)) return upperName.padStart(2, '0')

      // Attempt partial match for common issues (e.g. 'NCT of Delhi')
      if (upperName.includes('DELHI')) return '09'

      return '' // Blank if not found
    }

    // Pad numbers to required length (Truncate/Strip logic if needed? previous padNumber was padding with 0s)
    // For Batch Total we used formatDecimalAmount
    // For AY we used custom logic
    // For State Code we return string 09
    const padNumber = (num, length) => {
      // Keep existing padNumber logic for other fields if any
      return String(num || 0).padStart(length, '0')
    }

    let fileContent = ''
    let lineNumber = 1

    // Ensure profileData has required sections (safety check)
    const section1 = profileData?.section1 || {}
    const section2 = profileData?.section2 || {}
    const section3 = profileData?.section3 || {}

    // File Header Record (FH)
    // Per spec: Line^FH^NST^R^Date^Seq^Type^TAN^Batches^Tool^...hashes...
    fileContent += `${lineNumber}^FH^NS1^R^${fileCreationDate}^1^D^${section1.taxDeduction || ''}^1^Tax Eases Client^^^^^^^^\n`
    lineNumber++

    // Batch Header Record (BH)
    const batchTotal = challanData.reduce((sum, challan) => sum + parseFloat(challan.totalAmount || 0), 0)
    const deductorAddress1 = `${section2.flatNo || ''} ${section2.nameOfPremises || ''} ${section2.roadStreet || ''}`.trim()
    const responsiblePersonAddress1 = `${section3.flatNo || ''} ${section3.nameOfPremises || ''} ${section3.roadStreet || ''}`.trim()

    // Receipt Number Logic
    const receiptNo7 = selectedReturnType === 'Revised' ? (section1.receiptNoOriginal || '') : ''
    const receiptNo8 = selectedReturnType === 'Revised'
      ? (section1.receiptNoEarlier || '')
      : (section1.isEarlierReturnFiled === 'Yes' ? (section1.receiptNoOriginal || '') : '')

    const deductorStdCode = section2.stdCode || '91'
    const respPersonStdCode = (section3.sameAsAbove ? section2.stdCode : section3.stdCode) || section2.stdCode || '91'
    const respPersonTelephone = (section3.sameAsAbove ? section2.telephoneNo : (section3.telephone || section3.telephoneNo)) || ''

    fileContent += `${lineNumber}^BH^1^${challanData.length}^26Q^^^${receiptNo7}^${receiptNo8}^^^^`
    fileContent += `${section1.taxDeduction || ''}^^${section1.permanentAccountNumber || ''}^${assessmentYear}^${financialYear}^${selectedQuarter}^`
    fileContent += `${padString(section2.name || '', 75)}^${padString(section2.branchDivision || '', 75)}^`
    fileContent += `${padString(deductorAddress1, 25)}^${padString(section2.areaLocality || '', 25)}^`
    fileContent += `${padString(section2.townCity || '', 25)}^${padString('', 25)}^`
    fileContent += `${padString('', 25)}^${getStateCode(section2.state)}^${section2.pinCode || ''}^`
    fileContent += `${padString(section2.email || '', 75)}^${deductorStdCode}^${section2.telephoneNo || ''}^`
    fileContent += `N^${section1.typeOfDeduction || ''}^${padString(section3.name || '', 75)}^`
    fileContent += `${padString(section3.designation || '', 20)}^${padString(responsiblePersonAddress1, 25)}^`
    fileContent += `${padString(section3.areaLocality || '', 25)}^${padString(section3.townCity || '', 25)}^`
    fileContent += `${padString('', 25)}^${padString('', 25)}^`
    fileContent += `${getStateCode(section3.state)}^${section3.pinCode || ''}^`
    fileContent += `${padString(section3.email || '', 75)}^${section3.mobileNo || ''}^`
    fileContent += `${respPersonStdCode}^${respPersonTelephone}^`
    fileContent += `${section3.hasAddressChanged === 'Yes' ? 'Y' : 'N'}^${formatDecimalAmount(batchTotal)}^^^^N^${section1.isEarlierReturnFiled === 'Yes' ? 'Y' : 'N'}^^^^^^^${section3.permanentAccountNumber || ''}`
    fileContent += `${'^'.repeat(10)}${section2.gstin || ''}${'^'.repeat(3)}\n`
    lineNumber++

    // Challan Detail Records (CD)
    challanData.forEach((challan, challanIndex) => {
      const tdsEntriesForChallan = tdsData.filter(tds => tds.challanId == challan.id)

      // CD Record generation - Strict Mapping 1-41
      fileContent += `${lineNumber}^CD^1^${challanIndex + 1}^${tdsEntriesForChallan.length}^N^` // F1-F6
      fileContent += `^^^^^` // F7-F11 (Empty)
      fileContent += `${challan.challanSerialNo || ''}^` // F12 Challan No
      fileContent += `^^^` // F13-F15 (Empty/DDO)
      fileContent += `${challan.bsrCode}^` // F16 BSR
      fileContent += `^` // F17 (Empty)
      fileContent += `${formatDate(challan.dateOfDeposit)}^` // F18 Date
      fileContent += `^^^` // F19-F21 (Empty)

      // Amounts F22-F27
      const eduCess = parseFloat(challan.educationCess || 0) + parseFloat(challan.higherEducationCess || 0)
      fileContent += `${formatDecimalAmount(challan.tdsAmount)}^${formatDecimalAmount(challan.surcharge)}^${formatDecimalAmount(eduCess)}^` // F22-F24
      fileContent += `${formatDecimalAmount(challan.interest)}^${formatDecimalAmount(challan.other)}^` // F25-F26
      fileContent += `${formatDecimalAmount(challan.totalAmount)}^` // F27 Total

      fileContent += `^` // F28 (Empty)

      // Deductee Allocation F29-F35
      // Calc sums from tdsEntries
      const deducteeTotal = tdsEntriesForChallan.reduce((sum, tds) => sum + parseFloat(tds.tdsAmount || 0), 0) // Should verify if this maps to Total Deposit
      // Spec F29: Total Tax Deposit as per deductee annexure.
      // Spec F33: Sum of Total Income Tax Deducted. 
      // Usually F29 = Sum of (Income + Surch + Cess + Int + Other) allocations.
      // Here assuming tdsAmount includes all? Or is it just TAX?
      // For simplicity, mapping Tax to Tax, 0 to others if not tracked separately.

      fileContent += `${formatDecimalAmount(deducteeTotal)}^` // F29
      fileContent += `${formatDecimalAmount(deducteeTotal)}^` // F30 (Income Tax Alloc)
      fileContent += `${formatDecimalAmount(0)}^${formatDecimalAmount(0)}^` // F31-F32 (Surch, Cess Alloc - assuming 0)
      fileContent += `${formatDecimalAmount(deducteeTotal)}^` // F33 (Total Tax Alloc)
      fileContent += `${formatDecimalAmount(0)}^${formatDecimalAmount(0)}^` // F34-F35 (Int, Other Alloc)

      fileContent += `^` // F36 Cheque No (Empty)
      fileContent += `${challan.bookEntry ? 'Y' : 'N'}^` // F37 Book Entry
      fileContent += `^` // F38 Remarks
      fileContent += `${formatDecimalAmount(challan.feesAmount)}^` // F39 Fee
      fileContent += `${challan.minorHead || ''}^` // F40 Minor Head

      fileContent += `\n` // End of Line
      lineNumber++

      // Deductee Detail Records (DD)
      tdsEntriesForChallan.forEach((tds, tdsIndex) => {
        let deducteeCode = tds.deducteeCode || '02'
        // User Request: "1 for Companies / 2 for others". Map 01->1, 02->2.
        if (deducteeCode === '01') deducteeCode = '1'
        if (deducteeCode === '02') deducteeCode = '2'

        const rate = parseFloat(tds.rateOfTax || 0)
        const tdsVal = parseFloat(tds.tdsAmount || 0)
        const surchargeVal = parseFloat(tds.surcharge || 0)
        const cessVal = parseFloat(tds.cess || 0)
        const totalTax = tdsVal + surchargeVal + cessVal

        fileContent += `${lineNumber}^DD^1^${challanIndex + 1}^${tdsIndex + 1}^O^` // F1-F6
        fileContent += `^` // F7 (NA)
        fileContent += `${deducteeCode}^` // F8 Deductee Code
        fileContent += `^` // F9 (NA)
        fileContent += `${tds.panOfDeductee || 'PANNOTAVBL'}^` // F10 PAN
        fileContent += `^` // F11 (NA)
        fileContent += `${tds.deducteeReferenceNo || ''}^` // F12 Ref No
        fileContent += `${tds.firstName || ''} ${tds.middleName || ''} ${tds.lastName || ''}^` // F13 Name (Simplified concatenation)

        fileContent += `${formatDecimalAmount(tdsVal)}^${formatDecimalAmount(surchargeVal)}^${formatDecimalAmount(cessVal)}^` // F14-F16
        fileContent += `${formatDecimalAmount(totalTax)}^` // F17 Total TDS
        fileContent += `^` // F18 (NA)
        fileContent += `${formatDecimalAmount(totalTax)}^` // F19 Total Deposited (Matches Total TDS)
        fileContent += `^^` // F20, F21 (NA)

        fileContent += `${formatDecimalAmount(tds.amountOfPayment)}^` // F22 Amount Payment
        fileContent += `${formatDate(tds.dateOfPayment)}^` // F23 Date Payment
        fileContent += `${formatDate(tds.dateOfDeduction)}^` // F24 Date Deduction
        fileContent += `^` // F25 (NA)
        fileContent += `${rate.toFixed(4)}^` // F26 Rate
        fileContent += `^` // F27 (NA)
        fileContent += `^` // F28 Book Entry (Empty for >2013-14)
        fileContent += `^` // F29 (NA)
        // Section Code Mapping
        const sectionMap = {
          '193': '193', '194': '194', '194A': '94A', '194B': '94B', '194B-P': '4BP',
          '194BB': '4BB', '194C': '94C', '194D': '94D', '194EE': '4EE', '194F': '94F',
          '194G': '94G', '194H': '94H', '194I': '94I', '194I(a)': '4IA', '194I(b)': '4IB',
          '194LA': '94L', '194DA': '4DA', '194LBA': '4BA', '192A': '2AA', '194LBB': 'LBB',
          '194IA': '9IA', '194LBC': 'LBC', '194IC': '4IC', '194N': '94N', '194K': '94K',
          '194J(a)': '4JA', '194J': '4JA', // Default 194J to 4JA (Tech) if not specific? Or 94J? 
          // FVU 5.5+ removed 94J. Split into 4JA/4JB. 
          // I'll map '194J' to '4JA' as it's the most common default, but user should select specific.
          '194J(b)': '4JB', '194LBA(a)': 'BA1', '194LBA(b)': 'BA2', '194NF': '4NF',
          '194O': '94O', '194Q': '94Q', '194R': '94R', '194RP': '4RP', '194S': '94S',
          '194SP': '4SP', '194BA': '9BA', '194BA-P': '4AP', '194NC': '4NC', '194N-FT': '9FT',
          '194IB': '4IB', '194M': '94M'
        }
        const mappedSection = sectionMap[tds.sectionCode] || tds.sectionCode

        fileContent += `${tds.reasonForNonDeduction || ''}^` // F30 Remarks 1
        fileContent += `^^` // F31, F32 (NA)
        fileContent += `${mappedSection || ''}^` // F33 Section Code (Mapped)
        fileContent += `${tds.certificateNo197 || ''}^` // F34 Cert No

        fileContent += `^^^^^^^^` // F35-F42 (Fillers)
        fileContent += `^^^^^^` // F43-F48 (194N Optional - Empty)
        fileContent += `^^^^^` // F49-F53 (Fillers)
        // F54 Hash (Implicit empty at end of line? Or explicit caret?)
        // Standard: F53 Ends with Caret. F54 starts.
        // If F54 is empty, we stop. Or add caret?
        // Let's add caret to be safe if strictly 54 fields?
        // Spec says 54 fields.
        // If I put `^ ` at end of F53.
        // `...^ F53 ^ `.
        // If F54 is empty.
        // `...^ F53 ^ `. (Means F54 is empty string).
        // If I stop there, parsing gives 54 fields.
        // So NO EXTRA CARET.

        fileContent += `\n` // End of Line
        lineNumber++
      })
    })

    return fileContent
  }

  // Main function to handle Prepare Return
  const handlePrepareReturnFile = () => {
    // Debug: Log current profile data
    console.log('Current Profile Data:', profileData)
    console.log('TDS Data:', tdsData)
    console.log('Challan Data:', challanData)

    // Validate data
    const validationErrors = validateDataForReturn()

    if (validationErrors.length > 0) {
      addError('Please fix the following errors before preparing return:')
      validationErrors.forEach(error => {
        addError(`• ${error} `)
      })
      return
    }

    try {
      // Generate file content
      const fileContent = generateTdsStatementFile()

      // Create and download file
      const blob = new Blob([fileContent], { type: 'text/plain' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      const fileName = `${selectedForm}${selectedQuarter}${selectedFinancialYear.split('-')[1]}.txt`
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('TDS Statement file generated successfully!')
    } catch (error) {
      console.error('Error generating TDS statement:', error)
      addError('Error generating TDS statement file. Please try again.')
    }
  }

  // Function to handle file validation - Sends file content to server
  const shouldAutoValidateRef = React.useRef(false)

  const executeValidation = async (currentCsiFile) => {
    setIsValidating(true)

    try {
      // Generate file content
      const fileContent = generateTdsStatementFile()
      const tan = profileData?.section1?.taxDeduction || 'UNKNOWN_TAN'
      const fyParts = (selectedFinancialYear || '').split('-')
      const fyShort = (fyParts[1] || 'XX').slice(-2)
      let baseName = `${selectedForm || 'FORM'}${selectedQuarter || 'QX'}${fyShort}`
      if (baseName.length > 8) baseName = baseName.slice(0, 8)
      const fileName = `${baseName}.txt`

      // Read CSI file content as base64
      const reader = new FileReader()
      reader.onload = async (e) => {
        const csiFileContent = e.target.result

        try {
          const response = await axios.post('/api/validate-tds-file', {
            fileContent,
            fileName,
            tan,
            csiFileContent, // Base64 or text content
            csiFileName: currentCsiFile.name,
            consoFileContent: consoFileContent,
            isRevised: selectedReturnType === 'Revised',
            financialYear: selectedFinancialYear,
            quarter: selectedQuarter,
            formType: selectedForm || '26Q'
          })

          if (response.data.success) {
            // Show enhanced success notification with "Open Folder" button
            const outputDir = response.data.outputDir;

            toast.success(
              <div className="flex flex-col">
                <span className="font-semibold">{response.data.message}</span>
                {outputDir && (
                  <button
                    onClick={() => {
                      axios.post('/api/open-folder', { folderPath: outputDir })
                        .catch(err => console.error('Failed to open folder:', err));
                    }}
                    className="mt-2 text-xs bg-white text-green-700 font-bold py-1 px-2 rounded shadow hover:bg-green-50 flex items-center justify-center"
                  >
                    📂 Open Result Folder
                  </button>
                )}
                {response.data.logs && (
                  <details className="mt-2 text-[10px] text-white/90">
                    <summary className="cursor-pointer">View Logs</summary>
                    <pre className="mt-1 bg-black/20 p-1 rounded overflow-x-auto">
                      {response.data.logs.substring(0, 200)}...
                    </pre>
                  </details>
                )}
              </div>
              , {
                autoClose: 8000,
                closeOnClick: false,
                draggable: false
              });

          } else {
            toast.error(response.data.error || 'Validation failed')
          }
        } catch (apiError) {
          console.error('API Error:', apiError)
          toast.error(apiError.response?.data?.error || 'Failed to connect to validation server.')
        } finally {
          setIsValidating(false)
        }
      }

      reader.onerror = () => {
        toast.error('Failed to read CSI file')
        setIsValidating(false)
      }

      reader.readAsDataURL(currentCsiFile) // Read as base64 data URL

    } catch (error) {
      console.error('Validation error:', error)
      toast.error(error.message || 'An error occurred')
      setIsValidating(false)
    }
  }

  const handleValidateFile = async () => {
    // Validate data first
    const validationErrors = validateDataForReturn()
    if (validationErrors.length > 0) {
      toast.error('Please fix validation errors before validating file.')
      return
    }

    // Prepare for validation sequence
    setCsiFile(null)

    // Show confirmation popup instead of direct click
    setShowCsiUploadPopup(true)
  }

  // Validate File functionality - Event Listener
  useEffect(() => {
    const handleValidateTdsFileEvent = () => {
      // Intercept validation to ask for download
      setShowDownloadPrompt(true);
    }

    window.addEventListener('validateTdsFile', handleValidateTdsFileEvent)
    return () => window.removeEventListener('validateTdsFile', handleValidateTdsFileEvent)
  }, [handleValidateFile])


  // Conso File Parsing Logic
  const parseConsoFile = (fileContent) => {
    const lines = fileContent.split('\n')

    // Validate BH record form type
    const bhLine = lines.find(l => {
      const p = l.split('^')
      return p[1] === 'BH'
    })

    if (bhLine) {
      const bhParts = bhLine.split('^')
      const formInConso = (bhParts[4] || '').toUpperCase().trim()
      if (formInConso && formInConso !== '26Q' && formInConso !== '140') {
        toast.error(`Invalid Conso File! Expected Form 26Q, but file contains Form ${formInConso}.`)
        alert(`Invalid Conso File!\n\nThis form is Form 26Q, but the uploaded Conso file belongs to Form ${formInConso}.\nPlease upload the correct Form 26Q Conso file.`)
        return
      }
    }

    // Store content for validation
    setConsoFileContent(fileContent)

    const newChallans = []
    const newTdsData = []
    let currentChallan = null
    let rowIndex = 0

    try {
      lines.forEach((line) => {
        if (!line.trim()) return
        const parts = line.split('^')
        const recordType = parts[1] // FH, BH, CD, DD

        if (recordType === 'BH') {
          // Batch Header Parsing
          // State Code Map (ITD)
          const stateCodeMap = {
            '01': 'ANDAMAN AND NICOBAR ISLANDS', '02': 'ANDHRA PRADESH', '03': 'ARUNACHAL PRADESH', '04': 'ASSAM', '05': 'BIHAR',
            '06': 'CHANDIGARH', '07': 'DADRA AND NAGAR HAVELI', '08': 'DAMAN AND DIU', '09': 'DELHI', '10': 'GOA',
            '11': 'GUJARAT', '12': 'HARYANA', '13': 'HIMACHAL PRADESH', '14': 'JAMMU AND KASHMIR', '15': 'KARNATAKA',
            '16': 'KERALA', '17': 'LAKSHADWEEP', '18': 'MADHYA PRADESH', '19': 'MAHARASHTRA', '20': 'MANIPUR',
            '21': 'MEGHALAYA', '22': 'MIZORAM', '23': 'NAGALAND', '24': 'ORISSA', '25': 'PONDICHERRY',
            '26': 'PUNJAB', '27': 'RAJASTHAN', '28': 'SIKKIM', '29': 'TAMIL NADU', '30': 'TRIPURA',
            '31': 'UTTAR PRADESH', '32': 'WEST BENGAL', '33': 'CHHATTISGARH', '34': 'UTTARANCHAL', '35': 'JHARKHAND',
            '36': 'TELANGANA', '37': 'LADAKH', '99': 'FOREIGN'
          }

          const formType = parts[4]
          const fyRaw = parts[16] // FY
          const quarter = parts[17] // Quarter

          if (formType) {
            setSelectedForm(formType)
            localStorage.setItem('tdsForm', formType)
          }
          if (quarter) {
            setSelectedQuarter(quarter)
            localStorage.setItem('tdsQuarter', quarter)
          }
          if (fyRaw && fyRaw.length === 6) {
            const formattedFy = `${fyRaw.slice(0, 4)}-${fyRaw.slice(4, 6)}` // 2019-20
            setSelectedFinancialYear(formattedFy)
            localStorage.setItem('tdsFinancialYear', formattedFy)
          }

          const mapState = (code) => {
            const c = (code || '').toString().padStart(2, '0')
            return stateCodeMap[c] || code
          }

          setProfileData(prev => ({
            ...prev,
            section1: {
              ...prev.section1,
              taxDeduction: parts[12] || '', // TAN
              permanentAccountNumber: parts[14] || '', // PAN
              typeOfDeduction: parts[31] || '', // Type/Status (K)
              receiptNoOriginal: parts[7] || '', // 8th place
              receiptNoEarlier: parts[8] || '' // 9th place
            },
            section2: {
              ...prev.section2,
              name: parts[18] || '',
              branchDivision: parts[19] || '',
              flatNo: parts[20] || '',
              nameOfPremises: parts[21] || parts[22] || '', // Try both
              roadStreet: parts[22] || parts[23] || '',
              // Shift might be present here too?
              // Standard: 20(Flat), 21(Prem), 22(Road), 23(Area), 24(City), 25(State), 26(Pin), 27(Email)
              // User Img: Town/City "delhi" (24? or 25?), State "9" (25), Pin 110049 (26).
              // So 25, 26, 27 seem aligned.
              areaLocality: parts[23] || '',
              townCity: parts[24] || '',
              state: mapState(parts[25]),
              pinCode: parts[26] || '',
              email: parts[27] || '',
              stdCode: parts[28] || '',
              telephoneNo: parts[29] || ''
            },
            section3: {
              ...prev.section3,
              // Shifted indices hypothesis: Name at 32
              name: parts[32] || '',
              designation: parts[33] || '',
              flatNo: parts[34] || '',
              nameOfPremises: parts[35] || '',
              roadStreet: parts[36] || '',
              areaLocality: parts[37] || '',
              townCity: parts[38] || '',
              state: mapState(parts[39]),
              pinCode: parts[40] || '',
              email: parts[41] || '',
              mobileNo: parts[47] || '', // Validated from sample
              permanentAccountNumber: parts[58] || '', // Validated from sample: Index 58
              gstin: parts[69] || ''
            }
          }))
        } else if (recordType === 'CD') {
          // Challan Detail
          // Sample: 3^CD^1^1^20^N^^21^M^^^57858^^^^0510308^^29042022^^^^000000791663.00...
          // Map parts to challan object
          const challan = {
            id: Date.now() + rowIndex++,
            challanSerialNo: parts[11] || '', // F12
            bsrCode: parts[15] || '', // F16
            dateOfDeposit: parts[17] ? `${parts[17].slice(4, 8)} -${parts[17].slice(2, 4)} -${parts[17].slice(0, 2)} ` : '', // DDMMYYYY -> YYYY-MM-DD
            minorHead: parts[40] || '', // F40
            sectionCode: '', // CD doesn't strictly have Section Code in F1? 
            // Wait, CD F1-F6 are control.
            // Challan Amount usually F27 (Total).
            tdsAmount: parseFloat(parts[21] || 0), // F22 TDS
            surcharge: parseFloat(parts[22] || 0), // F23
            educationCess: parseFloat(parts[23] || 0), // F24 (Ed + HighEd)
            interest: parseFloat(parts[24] || 0), // F25
            other: parseFloat(parts[25] || 0), // F26
            totalAmount: parseFloat(parts[26] || 0), // F27
            feesAmount: parseFloat(parts[38] || 0), // F39
            bookEntry: parts[36] === 'Y', // F37
            recordNo: '',
            // Initialize others
            higherEducationCess: 0
          }
          currentChallan = challan
          newChallans.push(challan)

        } else if (recordType === 'DD') {
          // Deductee Detail - Corrected Indices based on Sample 1236
          if (!currentChallan) return

          const deductee = {
            id: Date.now() + rowIndex++,
            challanId: currentChallan.id,
            deducteeCode: parts[7] === '1' ? '01' : '02',
            panOfDeductee: parts[9] || '',
            nameOfDeductee: parts[12] || '',
            amountOfPayment: parseFloat(parts[13] || 0),
            dateOfPayment: parts[22] ? `${parts[22].slice(4, 8)} -${parts[22].slice(2, 4)} -${parts[22].slice(0, 2)} ` : '',
            dateOfDeduction: parts[23] ? `${parts[23].slice(4, 8)} -${parts[23].slice(2, 4)} -${parts[23].slice(0, 2)} ` : '',
            rateOfTax: parseFloat(parts[25] || 0).toFixed(2), // Raw value is %

            tdsAmount: parseFloat(parts[16] || 0),
            sectionCode: parts[33] || '',
            source: 'conso', // Mark as from Conso file
          }

          // Reverse Map Section Code
          const mapSection = (code) => {
            if (code === '94C') return '194C'
            if (code === '94Q') return '194Q'
            if (code === '94J') return '194J'
            if (code === '4JA') return '194J(a)'
            if (code === '4JB') return '194J(b)'
            if (code === '94H') return '194H'
            if (code === '4IB') return '194IB'
            if (code === '94A') return '194A'
            if (code === '94I') return '194I'
            return code
          }
          deductee.sectionCode = mapSection(deductee.sectionCode)

          newTdsData.push(deductee)
        }
      })

      // Post-process Challans to auto-fill Section Code from their Deductees
      newChallans.forEach(challan => {
        const matchingDeductee = newTdsData.find(tds => tds.challanId === challan.id)
        if (matchingDeductee && matchingDeductee.sectionCode) {
          if (!challan.sectionCode) {
            challan.sectionCode = matchingDeductee.sectionCode
          }
        }
      })

      setChallanData(newChallans)
      setTdsData(newTdsData)
      setOriginalTdsData(JSON.parse(JSON.stringify(newTdsData))) // Deep copy for comparison source
      toast.success('Conso File Uploaded and Parsed Successfully!')

    } catch (err) {
      console.error(err)
      toast.error('Error parsing conso file')
    }
  }

  const handleConsoFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => parseConsoFile(ev.target.result)
    reader.readAsText(file)
  }

  const handleCsiFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (!file.name.endsWith('.csi')) {
        toast.error('Please select a valid .csi file')
        return
      }
      setCsiFile(file)
      if (typeof setShowCsiUploadPopup === 'function') setShowCsiUploadPopup(false)
      toast.success(`CSI File selected: ${file.name}. Generating FVU...`)
      if (typeof executeValidation === 'function') {
        executeValidation(file)
      }
    }
  }

  // Financial Year, Quarter and Form options
  // Financial Year, Quarter and Form options
  const today = new Date()
  const currMonth = today.getMonth() // 0=Jan, 3=Apr
  const currYear = today.getFullYear()

  // Calculate Current FY Start Year (If Apr or later, FY starts this year. Else last year.)
  const currentFyStart = currMonth >= 3 ? currYear : currYear - 1
  const financialYearOptions = []
  for (let start = currentFyStart; start >= 2023; start--) {
    financialYearOptions.push(`${start}-${(start + 1).toString().slice(-2)}`)
  }
  // Generate Quarter Options based on selected FY
  const selectedFyStart = parseInt(selectedFinancialYear.split('-')[0])
  let quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4']

  if (selectedFyStart > currentFyStart) {
    quarterOptions = []
  } else if (selectedFyStart === currentFyStart) {
    // Filter quarters based on current month
    let maxQ = 0
    if (currMonth >= 3 && currMonth <= 5) maxQ = 1 // Apr-Jun (Q1)
    else if (currMonth >= 6 && currMonth <= 8) maxQ = 2 // Jul-Sep (Q2)
    else if (currMonth >= 9 && currMonth <= 11) maxQ = 3 // Oct-Dec (Q3)
    else maxQ = 4 // Jan-Mar (Q4)

    quarterOptions = quarterOptions.slice(0, maxQ)
  }
  const formOptions = ['24Q', '26Q', '27Q', '27EQ']

  // Dropdown options for profile form - matching original TdsProfile

  const yesNoOptions = ['Yes', 'No']

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      const initialProfile = {
        section1: {
          taxDeduction: '',
          permanentAccountNumber: '',
          typeOfDeduction: '',
          receiptNoOriginal: '',
          receiptNoEarlier: '',
          isEarlierReturnFiled: 'No'
        },
        section2: {
          name: '',
          branchDivision: '',
          flatNo: '',
          nameOfPremises: '',
          roadStreet: '',
          areaLocality: '',
          townCity: '',
          pinCode: '',
          state: '',
          email: '',
          stdCode: '',
          telephoneNo: ''
        },
        section3: {
          sameAsAbove: false,
          name: '',
          designation: '',
          permanentAccountNumber: '',
          nameOfPremises: '',
          flatNo: '',
          areaLocality: '',
          roadStreet: '',
          pinCode: '',
          townCity: '',
          telephone: '',
          telephoneAlternate: '',
          state: '',
          email: '',
          emailAlternate: '',
          mobileNo: '',
          hasAddressChanged: '',
          hasRegularStatement: ''
        }
      }

      setChallanData([])
      setTdsData([])
      setOriginalTdsData([])
      setProfileData(initialProfile)

      localStorage.removeItem(getLegacyStorageKey('26Q', 'ChallanData'))
      localStorage.removeItem(getLegacyStorageKey('26Q', 'TdsData'))
      localStorage.removeItem(getLegacyStorageKey('26Q', 'ProfileData'))
      localStorage.removeItem(getLegacyStorageKey('26Q', 'OriginalTdsData'))

      toast.success('All data cleared successfully')
    }
  }

  useEffect(() => {
    if (selectedReturnType === 'Correction' || selectedReturnType === 'Revised') {
      navigate('/tds-dashboard/26q-correction');
    }
  }, [selectedReturnType, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
      {/* Sidebar */}
      <TdsSidebar
        isOpen={sidebarOpen}
        isMobile={isMobile}
        onClose={handleSidebarClose}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onSidebarStateChange={handleSidebarStateChange}
        tdsData={tdsData}
        challanData={challanData}
        selectionComplete={!!(selectedFinancialYear && selectedQuarter && selectedReturnType)}
      />

      {/* Header */}
      <header className={`bg - white shadow - sm border - b transition - all duration - 300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''
        } `} style={{
          width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%'
        }}>
        <div className="w-full px-2 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleClearData}
                className="flex items-center text-red-600 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Data
              </button>
              <button
                onClick={() => {
                  // Clear all localStorage data
                  localStorage.removeItem(getLegacyStorageKey('26Q', 'ChallanData'))
                  localStorage.removeItem(getLegacyStorageKey('26Q', 'TdsData'))
                  localStorage.removeItem(getLegacyStorageKey('26Q', 'FilingData'))
                  localStorage.removeItem(getLegacyStorageKey('26Q', 'ProfileData'))
                  localStorage.removeItem('tdsFinancialYear')
                  localStorage.removeItem('tdsQuarter')
                  localStorage.removeItem('tdsForm')
                  localStorage.removeItem('SELECTED_RETURN_TYPE')

                  // Navigate to main page
                  navigate('/')
                }}
                className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`py-4 px-2 sm:px-4 lg:px-6 transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''
        } `} style={{
          width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          minHeight: 'calc(100vh - 4rem)'
        }}>


        {/* Financial Year and Form Selection */}
        <div className="mb-8 bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-start sm:items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Financial Year (FY) <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedFinancialYear}
                onChange={(e) => {
                  setSelectedFinancialYear(e.target.value)
                  localStorage.setItem('tdsFinancialYear', e.target.value)
                }}
                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
              >
                {financialYearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quarter <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedQuarter}
                onChange={(e) => {
                  setSelectedQuarter(e.target.value)
                  localStorage.setItem('tdsQuarter', e.target.value)
                }}
                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
              >
                {quarterOptions.map(quarter => (
                  <option key={quarter} value={quarter}>{quarter}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Form Type <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedForm}
                onChange={(e) => {
                  const val = e.target.value
                  setSelectedForm(val)
                  localStorage.setItem('tdsForm', val)
                  const targetPath = getTdsFormRoute(val, selectedReturnType, selectedFinancialYear)
                  navigate(targetPath)
                }}
                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
              >
                {formOptions.map(formVal => {
                  const f = formVal;
                  const actualForm = selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026
                    ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f)
                    : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '27EQ' ? '27EQ' : f);
                  return (
                    <option key={formVal} value={actualForm}>
                      {actualForm}
                    </option>
                  )
                })}
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Return Type <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedReturnType}
                onChange={(e) => {
                  const val = e.target.value
                  setSelectedReturnType(val)
                  localStorage.setItem('SELECTED_RETURN_TYPE', val)
                  const targetPath = getTdsFormRoute(selectedForm, val, selectedFinancialYear)
                  navigate(targetPath)
                }}
                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
              >
                <option value="">Select Return Type</option>
                <option value="Original">Original</option>
                <option value="Revised">Revised</option>
              </select>
            </div>

            {/* Upload Conso File Button - Only for Revised Returns */}
            {selectedReturnType === 'Revised' && (
              <div className="flex-shrink-0">
                <input
                  type="file"
                  accept=".tds"
                  onChange={handleConsoFileUpload}
                  className="hidden"
                  id="conso-upload-inline"
                />
                <label
                  htmlFor="conso-upload-inline"
                  className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors cursor-pointer shadow-sm text-sm"
                  title="Upload Consolidated File for Revised Return"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Conso
                </label>
              </div>
            )}

            <div className="flex-shrink-0">
              <button
                onClick={() => {
                  // Navigate to the selected form
                  navigate(`/tds-dashboard/${selectedForm.toLowerCase()}`)
                }}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium shadow-md"
              >
                Apply Selection
              </button>
            </div>
          </div>
        </div>

        {/* Receipt Details Section (Conditional) */}
        {(selectedReturnType === 'Revised' || selectedReturnType === 'Original') && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-8 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 border-b pb-2">Receipt Details <span className="text-red-500">*</span></h3>
            <div className="space-y-4">
              {/* REVISED RETURN CASE */}
              {selectedReturnType === 'Revised' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Receipt No. of Original Return (Auto-filled)
                    </label>
                    <input
                      type="text"
                      value={profileData?.section1?.receiptNoOriginal || ''}
                      disabled
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Receipt No. of earlier Statement (Auto-filled)
                    </label>
                    <input
                      type="text"
                      value={profileData?.section1?.receiptNoEarlier || ''}
                      disabled
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                </div>
              )}

              {/* ORIGINAL RETURN CASE */}
              {selectedReturnType === 'Original' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Is earlier Return Filed?
                    </label>
                    <select
                      value={profileData?.section1?.isEarlierReturnFiled || 'No'}
                      onChange={(e) => handleProfileInputChange('section1.isEarlierReturnFiled', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                  {profileData?.section1?.isEarlierReturnFiled === 'Yes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Receipt No. of Original Return <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={profileData?.section1?.receiptNoOriginal || ''}
                        onChange={(e) => handleProfileInputChange('section1.receiptNoOriginal', e.target.value)}
                        required
                        className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                        placeholder="Enter 15-digit Receipt Number"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}





        {/* Section Content */}
        <div className="space-y-6">
          {activeSection === 'profile' && (
            <TdsProfileSection
              profileData={profileData}
              isProfileEditing={isProfileEditing}
              onProfileDataChange={handleProfileInputChange}
              onPrefillClick={() => setShowPrefillPrompt(true)}
              selectedReturnType={selectedReturnType}
            />
          )}

          {activeSection === 'tds-details' && (
            <TdsDetailsTable
              tdsData={tdsData}
              challanData={challanData}
              onTdsDataChange={handleTdsDataChange}
              selectedFinancialYear={selectedFinancialYear}
              selectedQuarter={selectedQuarter}
              isRevised={selectedReturnType === 'Revised'}
              originalData={originalTdsData}
            />
          )}

          {activeSection === 'challan-details' && (
            <ChallanDetailsTable
              challanData={challanData}
              tdsData={tdsData}
              onChallanDataChange={handleChallanDataChange}
              isRevised={selectedReturnType === 'Revised'}
              tan={profileData?.section1?.taxDeduction}
            />
          )}





          {activeSection === 'generated-files' && (






            <TdsGeneratedFiles isEmbedded={true} />






          )}






        </div>

      </main>

      {/* CSI Confirmation Modal */}
      {showCsiUploadPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl animate-fade-in border border-gray-100">
            <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Upload CSI File</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              To proceed with validation, you need to upload the <strong>Challan Status Inquiry (CSI)</strong> file.
              <br /><br />
              Please ensure you have downloaded it from the Income Tax portal.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowCsiUploadPopup(false)}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowCsiUploadPopup(false)
                  if (csiFileInputRef.current) {
                    csiFileInputRef.current.value = ''
                    csiFileInputRef.current.click()
                  }
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center transition-colors duration-200 shadow-md"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload CSI File
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        type="file"
        ref={csiFileInputRef}
        style={{ display: 'none' }}
        accept=".csi"
        onChange={handleCsiFileChange}
      />

      {/* CSI Download Prompt Modal */}
      {showDownloadPrompt && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <Upload className="w-5 h-5 mr-2 text-blue-600" />
              Download CSI File
            </h3>

            <p className="text-gray-600 mb-4 text-sm">
              Would you like to automatically download the latest CSI file from the Income Tax Portal?
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Password</label>
              <div className="relative">
                <input
                  type={showDownloadPassword ? "text" : "password"}
                  className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={downloadPassword}
                  onChange={(e) => setDownloadPassword(e.target.value)}
                  placeholder="Enter Password"
                />
                <button
                  type="button"
                  onClick={() => setShowDownloadPassword(!showDownloadPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showDownloadPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">TAN: {profileData.section1.taxDeduction}</p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCsiDownload}
                disabled={isDownloadingCsi}
                className={`flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center border border-transparent shadow-md ${isDownloadingCsi ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isDownloadingCsi ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Downloading...
                  </>
                ) : (
                  <span className="font-medium">Download & Continue</span>
                )}
              </button>
              <button
                onClick={handleSkipDownload}
                disabled={isDownloadingCsi}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition"
              >
                Skip (I have file)
              </button>
            </div>
            <button
              onClick={() => setShowDownloadPrompt(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
      {/* Validation Lock Screen Overlay */}
      {isValidating && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-900 bg-opacity-80 backdrop-blur-sm cursor-wait">
          <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-lg text-center transform transition-all scale-100">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-orange-100 rounded-full animate-ping opacity-75"></div>
              <div className="relative bg-white rounded-full p-2">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200 border-t-orange-600"></div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-800 mb-2">Validation in Progress</h3>
            <p className="text-gray-600 mb-6">
              Please wait while we validate your return.
            </p>
            <button
              onClick={handleCancelValidation}
              className="px-6 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors border border-red-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Profile Pre-fill Prompt Modal */}
      {showPrefillPrompt && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-blue-600" />
              Pre-fill Profile Data
            </h3>

            <p className="text-gray-600 mb-4 text-sm">
              We will automatically extract your PAN, Email, Mobile and Address from the Income Tax Portal.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Portal Password</label>
              <div className="relative">
                <input
                  type={showPrefillPassword ? "text" : "password"}
                  className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={prefillPassword}
                  onChange={(e) => setPrefillPassword(e.target.value)}
                  placeholder="Enter Portal Password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPrefillPassword(!showPrefillPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showPrefillPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">TAN: {profileData.section1.taxDeduction}</p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleProfilePrefill}
                disabled={isExtractingProfile || !prefillPassword}
                className={`flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center border border-transparent shadow-md ${(isExtractingProfile || !prefillPassword) ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isExtractingProfile ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Get Data...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Get Data
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowPrefillPrompt(false);
                  setPrefillPassword('');
                }}
                disabled={isExtractingProfile}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
            </div>
            {!isExtractingProfile && (
              <button
                onClick={() => setShowPrefillPrompt(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  )
}

export default Tds26QForm
